import React from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  selectMerchant,
  selectBalance,
  selectMode,
  selectIsLive,
} from "../../../redux/merchantSlice";

const P = {
  white: "#FFFFFF", snow: "#FAFAFA", pearl: "#F5F5F0", cloud: "#E8E5DE",
  mist: "#D4D0C8", silver: "#B0ADA6", steel: "#8A8780", graphite: "#5C5955",
  ink: "#1A1815", gold: "#C9A84C", goldDark: "#9A7A2E",
  success: "#2D8B5F", successBg: "#E8F5EE", error: "#C44D4D", warn: "#D4952B", warnBg: "#FEF4E5",
};
const serif = "'Playfair Display', Georgia, serif";
const GOLD_GRAD = "linear-gradient(135deg, #E8C96A, #C9A84C, #9A7A2E)";

const fmt = (n: number, c = "eur") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: c.toUpperCase(), minimumFractionDigits: 2 }).format(n); }
  catch { return `${c.toUpperCase()} ${n.toFixed(2)}`; }
};

export default function Balances() {
  const merchant = useSelector(selectMerchant);
  const balance  = useSelector(selectBalance);
  const mode     = useSelector(selectMode);
  const isLive   = useSelector(selectIsLive);
  const curr     = merchant?.currency || "eur";

  const availArr = balance?.available || [];
  const pendArr  = balance?.pending   || [];
  const totalAvail = availArr.reduce((s: number, b: any) => s + (b.amount || 0), 0);
  const totalPend  = pendArr.reduce((s: number, b: any) => s + (b.amount || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 24, fontFamily: serif, fontWeight: 700, color: P.ink, marginBottom: 6 }}>Balances</h2>
        <p style={{ fontSize: 13, color: P.steel }}>Overview of your account balances in {mode} mode.</p>
      </div>

      {/* Balance Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginBottom: 36 }}>
        {[
          { label: "Available Balance", value: totalAvail, icon: "◈", color: P.success, bg: P.successBg },
          { label: "Pending Balance",   value: totalPend,  icon: "◇", color: P.warn,    bg: P.warnBg },
          { label: "Total Balance",     value: totalAvail + totalPend, icon: "✦", color: P.gold, bg: `${P.gold}0D` },
        ].map((card, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            style={{
              background: P.white, borderRadius: 14, padding: "28px 24px",
              border: `1px solid ${P.cloud}`, position: "relative", overflow: "hidden",
            }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: i === 2 ? GOLD_GRAD : card.color, opacity: 0.6 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: card.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: card.color }}>{card.icon}</div>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: P.steel, textTransform: "uppercase" as const }}>{card.label}</span>
            </div>
            <div style={{ fontSize: 32, fontFamily: serif, fontWeight: 700, color: P.ink, letterSpacing: "-0.02em" }}>
              {fmt(card.value / 100, curr)}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Balance breakdown */}
      <div style={{ background: P.white, borderRadius: 14, border: `1px solid ${P.cloud}`, overflow: "hidden" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${P.pearl}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>Balance by Currency</div>
        </div>
        {availArr.length === 0 && pendArr.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.25 }}>◈</div>
            <div style={{ fontSize: 13, color: P.silver }}>No balance data available yet.</div>
          </div>
        ) : (
          <div style={{ padding: "4px 0" }}>
            {availArr.map((b: any, i: number) => (
              <div key={i} style={{ padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${P.pearl}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: P.gold, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>{b.currency}</span>
                  <span style={{ fontSize: 12, color: P.silver }}>Available</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: P.ink }}>{fmt(b.amount / 100, b.currency)}</span>
              </div>
            ))}
            {pendArr.map((b: any, i: number) => (
              <div key={`p-${i}`} style={{ padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${P.pearl}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: P.warn, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>{b.currency}</span>
                  <span style={{ fontSize: 12, color: P.silver }}>Pending</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: P.ink }}>{fmt(b.amount / 100, b.currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}